import { useEffect } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Alert } from "@mui/material";

import { PlantConstraint } from "../../../../../api/types/plants";
import { useApiValidation } from "../../../../../hooks/useApiValidation";
import { useFetchData } from "../../../../../hooks/useFetchData";
import { SectionProps } from "../../../../../types/section-props";
import { useConstraintsData } from "../../../api/useConstraintsData";
import { useConstraintsUpdate } from "../../../api/useConstraintsUpdate";
import { CafsSection } from "../../../const/cafs-section";
import { useSectionSaveDisabled } from "../../../hooks/useSectionSaveDisabled";
import { mapInitialValues } from "../../../utils/map-initial-values";
import { renderCafsTableRow } from "../../../utils/render-cafs-table-row";
import { CafsTable } from "../../cafs-table/CafsTable";
import { SectionRemarks } from "../SectionRemarks";

import { constraintsSchema } from "./constraints-schema";
import { useConstrainstVariables } from "./useConstraintsVariables";

export function ConstraintsSection({ expanded }: SectionProps) {
  const variables = useConstrainstVariables();

  const { shouldFetchData } = useFetchData(expanded);

  const { constraintsData, isPending } = useConstraintsData(shouldFetchData);

  const { updateConstraints, apiErrors } = useConstraintsUpdate({
    onSuccess: () => {},
  });

  const methods = useForm<FieldValues>({
    resolver: yupResolver(constraintsSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (constraintsData) {
      methods.reset(mapInitialValues(constraintsData, variables));
    }
  }, [constraintsData]);

  const handleSubmit = (data: FieldValues) => {
    updateConstraints(data);
  };

  useApiValidation(apiErrors, methods);

  const { disabled } = useSectionSaveDisabled(methods, isPending);

  return constraintsData && !isPending ? (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <SectionRemarks<PlantConstraint>
          name="ConstraintsRemarks"
          data={constraintsData}
        />
        <CafsTable<PlantConstraint>
          section={CafsSection.PlantConstraint}
          rows={variables}
          render={renderCafsTableRow}
          data={constraintsData ?? {}}
          saveDisabled={disabled}
        />
      </form>
    </FormProvider>
  ) : (
    <div className="w-full">
      <Alert severity="info">Loading data</Alert>
    </div>
  );
}
